import { useQuery } from "@tanstack/react-query";
import { CoordinateExtent, Edge, Node } from "@xyflow/react";
import ELK from "elkjs/lib/elk.bundled.js";

const formatElkEdge = (edge: Edge) => {
  return {
    id: String(edge.id),
    sources: [String(edge.source)],
    targets: [String(edge.target)],
  };
};

const createElkGroup = (
  node: Node,
  children: Node[],
  edges: Edge[],
  {
    childSpacing = 100,
    direction = "DOWN",
  }: {
    childSpacing?: number;
    direction?: "DOWN" | "UP" | "RIGHT" | "LEFT";
  }
) => {
  return {
    id: node.id,
    width: Number(node.measured?.width),
    height: Number(node.measured?.height),
    layoutOptions: {
      "elk.direction": direction,
      "elk.spacing.nodeNode": `${childSpacing}`,
      "elk.layered.spacing.nodeNodeBetweenLayers": `${childSpacing + 20}`,
      "elk.padding": "[top=60,left=30,bottom=30,right=30]",
    },
    children: children.map((child) => ({
      id: child.id,
      width: Number(child.measured?.width),
      height: Number(child.measured?.height),
      layoutOptions: {
        "elk.direction": direction,
      },
    })),
    edges: edges.map((edge) => formatElkEdge(edge)),
  };
};
const createElkNode = (node: Node) => {
  return {
    id: node.id,
    width: Number(node.measured?.width),
    height: Number(node.measured?.height),
    layoutOptions: {
      "elk.alignment": "TOP",
      "elk.layered.layering.layerChoiceConstraint": 0,
    },
  };
};

const createElkGraph = async (
  nodes: Node[],
  edges: Edge[],
  { direction = "DOWN" }: { direction?: "DOWN" | "UP" | "RIGHT" | "LEFT" }
) => {
  const elk = new ELK();

  const opts = {
    childSpacing: 100,
    direction,
    childWidth: 280,
    nodeWidth: 350,
  };

  const elkNodes = nodes
    .filter((n) => !n.parentId)
    .map((node) => {
      const childNodes = nodes.filter((n) => n.parentId === node.id);
      if (childNodes.length) {
        const childEdges = edges.filter((e) => e.data?.parentId === node.id);
        return createElkGroup(node, childNodes, childEdges, opts);
      } else {
        return createElkNode(node);
      }
    });

  // Solo edge globali (non tra children dello stesso parent)
  const elkEdges = edges
    .filter((e) => !e.data?.parentId)
    .map((edge) => formatElkEdge(edge));

  const elkGraph = {
    id: "root",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction": direction,
      "elk.alignment": "TOP",
      "elk.contentAlignment": "V_CENTER H_CENTER",
      "elk.layered.spacing.nodeNodeBetweenLayers": "60",
      "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
      "elk.layered.layering.layerConstraint": "FIRST",
      "elk.layered.nodePlacement.strategy": "BRANDES_KOEPF",
      "elk.spacing.edgeNode": "20",
      "elk.spacing.edgeEdge": "20",
      "elk.spacing.nodeNode": "200",
      "elk.partitioning.activate": "true",
    },
    children: elkNodes,
    edges: elkEdges,
  };

  return await elk.layout(elkGraph);
};

function applyLayoutToParentNode<T extends Record<string, unknown>>(
  node: Node<T>,
  elkGroup: any
) {
  return {
    ...node,
    position: { x: elkGroup.x || 0, y: elkGroup.y || 0 },
    style: {
      ...node.style,
      width: "auto",
      height: "auto",
      opacity: 1,
    },
  };
}

function applyLayoutToChildNode<T extends Record<string, unknown>>(
  childNode: Node<T>,
  elkChild: any
) {
  return {
    ...childNode,
    position: { x: elkChild.x || 0, y: elkChild.y || 0 },
    parentId: childNode.parentId,
    style: {
      ...childNode.style,
      width: elkChild.width,
      height: "auto",
      opacity: 1,
    },
    extent: "parent" as CoordinateExtent | "parent" | null | undefined,
  };
}

async function createGraphLayout<T extends Record<string, unknown>>(
  nodes: Node<T>[],
  edges: Edge[],
  { direction }: { direction?: "DOWN" | "UP" | "RIGHT" | "LEFT" }
) {
  const layoutedGraph = await createElkGraph(nodes, edges, { direction });
  const layoutedNodes: Node<T>[] = [];

  layoutedGraph?.children?.forEach((elkGroup) => {
    const parentNode = nodes.find((n) => n.id === elkGroup.id);
    if (!parentNode) {
      console.warn("Parent node not found for ELK group:", elkGroup.id);
      return;
    }

    layoutedNodes.push(applyLayoutToParentNode(parentNode, elkGroup));

    elkGroup.children?.forEach((elkChild) => {
      const childNode = nodes.find((n) => n.id === elkChild.id);
      if (childNode) {
        layoutedNodes.push(applyLayoutToChildNode(childNode, elkChild));
      }
    });
  });

  return layoutedNodes;
}

export async function runLayout<T extends Record<string, unknown>>(
  nodes: Node<T>[],
  edges: Edge[],
  { direction = "DOWN" }: { direction?: "DOWN" | "UP" | "RIGHT" | "LEFT" }
) {
  try {
    return await createGraphLayout(nodes, edges, { direction });
  } catch (error) {
    console.error("ELK hierarchical layout failed:", error);
    // Fallback semplice
    const fallbackNodes = nodes.map((node, index) => ({
      ...node,
      position: {
        x: 50 + (index % 3) * 250,
        y: 50 + Math.floor(index / 3) * 150,
      },
    }));
    return fallbackNodes;
  }
}

export function useLayout<T extends Record<string, unknown>>(
  nodes: Node<T>[],
  edges: Edge[],
  {
    direction = "DOWN",
    nodesInitialized = false,
  }: {
    direction?: "DOWN" | "UP" | "RIGHT" | "LEFT";
    nodesInitialized?: boolean;
  }
) {
  return useQuery({
    queryKey: [
      "cityEscapeFlowChartLayout",
      nodes,
      edges,
      direction,
      nodesInitialized,
    ],
    queryFn: async () => {
      return await runLayout(nodes, edges, { direction });
    },
    enabled: nodesInitialized,
  });
}
